/**
 * Block-child `ACAD_TABLE` render arm (@codex PR #785 round 1, F1).
 *
 * An ACAD_TABLE is a legal block-definition child (`blockChildEntitySchema`
 * carries `tableEntitySchema`; the BLOCKS section has the identical structure
 * to ENTITIES per the Autodesk DXF reference / ezdxf `BlockLayout`), so an
 * INSERT of such a definition must draw the whole table — every cell MTEXT and
 * every grid LINE — placed by the INSERT's affine like any other child.
 *
 * # Why this is a COMPOSITION, not a transformed clone
 *
 * `ACAD_TABLE` IS a block reference: its first subclass is
 * `AcDbBlockReference`, group 2 names the anonymous `*T<N>` block holding the
 * table's rendered graphics and 10/20/30 is the insertion point. What AutoCAD
 * draws is that block's content. Crucially, `AcDbTable` carries NO scale
 * factors — its only placement fields are the insertion point and the
 * horizontal-direction vector — so a transformed CLONE of the entity could
 * carry the enclosing INSERT's translation and rotation but could not carry
 * its SCALE at all: a table inside a ×2 INSERT would draw at ×1, half its real
 * size and no longer aligned with the block's other geometry.
 *
 * So the placement is composed instead: the table adapter is run on the
 * block-LOCAL table, and the block-content reference it asks for is composed
 * with the enclosing INSERT affine before the ONE real flatten runs — exactly
 * the technique `./blockChildMultileader.ts` uses for a block-child MLEADER's
 * own block content, through the very same
 * {@link composeBlockContentReference} (`M_insert ∘ M_content`, mirror,
 * non-uniform scale and the shear-carrying inner rotation included). One
 * truth for "compose an enclosing INSERT onto a nested reference", so the two
 * arms cannot drift.
 *
 * # Import-cycle note
 *
 * This module imports `tableFromEntity`; `blockFromEntity.ts` imports this
 * module. The reverse edge (table → block flatten) is INJECTED as a
 * {@link BlockContentFlattener}, never imported — see
 * `./blockReferenceFlatten.ts` for the full rationale and the
 * dependency-cruiser zero-cycle constraint it satisfies.
 */

import type { BlockDefinition, TableEntity } from "@dxf-ai-editor/drawing-model";

import { applyOffset } from "../../coordinateOrigin";
import type { RenderableEntity, Vec3 } from "../../types";

import type { AdapterContext } from "../adapterContext";

import { composeBlockContentReference } from "./blockChildMultileader";
import type {
  BlockContentFlattener,
  BlockReferenceFlattenArgs,
} from "./blockReferenceFlatten";
import {
  bakeBlockChildLocal,
  type BlockInsertTransform,
} from "./blockTransform";
import { tableFromEntity } from "./tableFromEntity";

/**
 * Render ONE block-child ACAD_TABLE: run the production table adapter on the
 * block-LOCAL table and compose the enclosing INSERT affine onto the `*T<N>`
 * content reference it emits (module doc).
 *
 * `blocks` is threaded onto the adapter context so the `*T<N>` definition
 * resolves against the SAME block table the enclosing flatten walks (a render
 * branch that passes its block definitions explicitly may leave `ctx.blocks`
 * unset — without this the table's content would silently vanish on that
 * branch only, the identical guard the MLEADER arm carries).
 *
 * Returns `null` exactly when the top-level adapter does — an unresolvable or
 * empty `*T<N>` block draws NOTHING rather than a fabricated box (the honest
 * absence `tableEntitySchema` documents).
 */
export function renderBlockChildTable(args: {
  child: TableEntity;
  basePoint: Vec3;
  t: BlockInsertTransform;
  ctx: AdapterContext;
  blocks: Record<string, BlockDefinition>;
  identity: { id: string; spaceId: string; parentLayer: string };
  flattenBlockContent: BlockContentFlattener;
}): RenderableEntity | null {
  const { child, basePoint, t, ctx, blocks, identity } = args;
  // Per-instance identity + AutoCAD layer-"0" inheritance + the enclosing block
  // table on the context, through the ONE helper the MLEADER arm uses
  // ({@link bakeBlockChildLocal}). Every coordinate stays AUTHORED — that is
  // what makes the composition below the single, exact transform.
  const { local, adapterCtx } = bakeBlockChildLocal<TableEntity>(
    child,
    identity,
    ctx,
    blocks,
  );

  // A mutable HOLDER, not a `let`: the composed reference is assigned inside
  // the callback below, which TypeScript's control-flow analysis cannot see —
  // a plain `let` would still be narrowed to its `null` initialiser at every
  // read after the adapter call.
  const captured: { reference: BlockReferenceFlattenArgs | null } = {
    reference: null,
  };
  const composingFlatten: BlockContentFlattener = (contentArgs) => {
    // `contentArgs` is the table's OWN reference in the block-local frame
    // (insertion = the table's 10/20/30, unit scale, rotation from group
    // 11/21/31). Composing it here — rather than rewriting the entity — keeps
    // the layer / colour / linetype thread the adapter folded in.
    const world = composeBlockContentReference(contentArgs, basePoint, t);
    captured.reference = world;
    return args.flattenBlockContent(world);
  };

  const renderable = tableFromEntity(local, adapterCtx, composingFlatten);
  const reference = captured.reference;
  if (!renderable || renderable.geometry.kind !== "block-insert" || !reference) {
    return renderable;
  }
  // The children are already world-space (they came out of the composed
  // reference) and so are the wrapper `bounds` the adapter derived from them.
  // The wrapper's own transform scalars, however, are the ones the adapter read
  // off the LOCAL table — and they are live data, not decoration: `lod/
  // visibleSet.ts` depth-sorts a `block-insert` by `geometry.insertion.z`. Re-
  // state them from the composed world reference so the wrapper describes where
  // its children actually are.
  return {
    ...renderable,
    geometry: {
      ...renderable.geometry,
      insertion: applyOffset(reference.insertion),
      scale: reference.scale,
      rotation: reference.rotationDeg,
    },
  };
}
